import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Clock3, Pencil, Trash2 } from 'lucide-react';
import {
  CALENDAR_EVENT_META,
  formatEventDateRange,
  type CalendarEventItem,
} from '../calendar-utils';

type EventListProps = {
  title: string;
  events: CalendarEventItem[];
  onOpenDetail: (event: CalendarEventItem) => void;
  onEditEvent: (event: CalendarEventItem) => void;
  onDeleteEvent: (event: CalendarEventItem) => void;
  deletingEventId: number | null;
};

export const EventList = ({
  title,
  events,
  onOpenDetail,
  onEditEvent,
  onDeleteEvent,
  deletingEventId,
}: EventListProps) => {
  return (
    <Card className="border-border/60 shadow-sm">
      <CardHeader>
        <CardTitle className="text-lg">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {events.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No events available for this range.
          </p>
        )}
        {events.map((event) => {
          const meta = CALENDAR_EVENT_META[event.type];
          const canManage = event.type === 'personal' || event.type === 'invited';
          return (
            <div
              key={`${event.type}-${event.id}`}
              className="group rounded-lg border bg-card p-4 transition-colors hover:bg-muted/50"
            >
              <div
                role="button"
                tabIndex={0}
                onClick={() => onOpenDetail(event)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onOpenDetail(event);
                  }
                }}
              >
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <h3 className="truncate font-medium text-foreground">{event.title}</h3>
                    <Badge variant="outline" className={meta.badgeClassName}>
                      {meta.label}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    {canManage && (
                      <>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full"
                          aria-label="Edit event"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditEvent(event);
                          }}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 rounded-full text-destructive/70 hover:text-destructive"
                          aria-label="Delete event"
                          disabled={deletingEventId === event.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteEvent(event);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                <div className="mb-2 flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock3 className="h-4 w-4" />
                  <span>{formatEventDateRange(event.startDate, event.endDate)}</span>
                </div>
                <div className="mt-3 text-sm font-medium text-primary">View details</div>
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};
